import React, { useState, useEffect } from"react";
import { ChevronDown, ChevronRight, BookOpen, Search, Command, Lock } from"lucide-react";
import useEliteTechnologies, { LEGACY_FREE_TECH_IDS } from "../hooks/useEliteTechnologies";
import { legacyIsLocked } from "./elite/eliteTopicSequence";

/**
 * `nexusMode` + `accessStates` are TSG Nexus only. When set, the per-page
 * state comes from the server (GET /api/elite/pages/:slug) instead of the
 * legacy "levels 1-5 are free" heuristic:
 *
 *   FREE / UNLOCKED → open
 *   AVAILABLE       → open, but reading it spends one paid page
 *   LOCKED          → needs a purchase
 *
 * Both default to off, so the learner app and the college portals render
 * exactly as before.
 */
const SidebarTreeView = ({ data = [], onSelectSubtopic, theme ="dark", selectedFile = "", isPaid = false, isPurple = false, nexusMode = false, accessStates = null }) => {
  const host = window.location.hostname;
  const isTenantHost = host !== 'theskillgenie.com' && 
                      host !== 'www.theskillgenie.com' && 
                      host !== 'localhost' &&
                      host !== '127.0.0.1';

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isCollegeUser = (user.role === 'student' || user.role === 'tenant-student' || user.role === 'admin') && 
                        (user.tenant || user.college_id || isTenantHost);

  const hasFullAccess = isPaid || isCollegeUser;
  
  const [openTech, setOpenTech] = useState({});
  const [openTopic, setOpenTopic] = useState({});
  const [search, setSearch] = useState("");
  const [activeTech, setActiveTech] = useState("");
  const isDark = theme === "dark";

  // Get completion data for all technologies. Elite catalog is admin-managed
  // (see EliteTechnologyManager) — any active technology's slug is a valid
  // `completed_docs_<slug>` key, so this stays correct without a code change
  // when a technology is added.
  const [completionData, setCompletionData] = useState({});
  const { technologies: eliteTechs, loading: eliteTechsLoading } = useEliteTechnologies();

  useEffect(() => {
    if (eliteTechsLoading) return;
    const data = {};
    const techs = [...LEGACY_FREE_TECH_IDS, ...eliteTechs.map(t => t.id)];
    techs.forEach(t => {
      data[t] = JSON.parse(localStorage.getItem(`completed_docs_${t}`) || "[]");
    });
    setCompletionData(data);
  }, [selectedFile, eliteTechsLoading, eliteTechs]); // Refresh when selectedFile changes (likely after completion)

  // Check if we should hide the technology level (Elite Mastery case)
  const hideTechHeader = data.length === 1 && (data[0].technology.includes("Elite") || data[0].technology === "AI");

  useEffect(() => {
    if (hideTechHeader && data[0]) {
      setOpenTech((prev) => ({ ...prev, [data[0].technology]: true }));
      setActiveTech(data[0].technology);
    }
  }, [hideTechHeader, data]);

  const toggleTech = (tech) => {
    setOpenTech((prev) => ({ ...prev, [tech]: !prev[tech] }));
    setActiveTech(tech);
  };

  const toggleTopic = (topic) => {
    setOpenTopic((prev) => ({ ...prev, [topic]: !prev[topic] }));
  };

  // Auto-open parent tech and topic if a file is selected (e.g. on load)
  React.useEffect(() => {
    if (selectedFile) {
      const techMatch = data.find(tech => 
        tech.topics.some(t => t.subtopics.some(s => s.file === selectedFile))
      );
      if (techMatch) {
        setOpenTech(prev => ({ ...prev, [techMatch.technology]: true }));
        setActiveTech(techMatch.technology);
        const topicMatch = techMatch.topics.find(t => 
          t.subtopics.some(s => s.file === selectedFile)
        );
        if (topicMatch) {
          setOpenTopic(prev => ({ ...prev, [topicMatch.topic]: true }));
        }
      }
    }
  }, [selectedFile, data]);

 // Match the structure without sorting alphabetically
 const filteredData = data.filter(({ technology, topics }) => {
 if (!search) return true;
 return (
 technology.toLowerCase().includes(search.toLowerCase()) ||
 topics.some((t) =>
 t.topic.toLowerCase().includes(search.toLowerCase())
 )
 );
 });

 return (
    <div
      className={`h-full px-4 pb-6 w-full overflow-y-auto max-h-screen transition-all duration-300 relative custom-scrollbar shadow-2xl ${
        isDark ? "bg-[#1E1B4B] text-white" : (isPurple ? "bg-[#5B21B6] text-white" : "bg-[#F8FAFC] text-slate-800")
      }`}
    >
      <div className={`sticky top-0 z-20 ${isDark ? "bg-[#1E1B4B]/95 backdrop-blur-md" : (isPurple ? "bg-[#5B21B6]/95 backdrop-blur-md" : "bg-[#F8FAFC]/95 backdrop-blur-md")} pt-6 pb-4`}>
        <div className="flex items-center gap-3 mb-6">
           <div className="w-10 h-10 rounded-2xl bg-white flex items-center justify-center shadow-lg shadow-white/10">
              <BookOpen size={20} className="text-[#5B21B6]" />
           </div>
           <div>
             <h2 className="text-lg font-black text-white uppercase tracking-tighter leading-tight">
               Tech Library
             </h2>
             <p className="text-[10px] font-bold text-white/60 uppercase tracking-widest">Knowledge Base</p>
           </div>
        </div>

        <div className="relative group">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search topics..."
            className={`w-full pl-11 pr-12 py-3.5 text-sm rounded-2xl outline-none transition-all duration-300 shadow-sm border ${
              isDark
                ? "bg-white/10 text-white placeholder-white/40 border-white/20 focus:border-white/50 focus:bg-white/20 focus:ring-4 focus:ring-white/10"
                : (isPurple 
                    ? "bg-white text-black placeholder-white/60 border-white/20 focus:border-white/50 focus:bg-white/20 focus:ring-4 focus:ring-white/10"
                    : "bg-white text-slate-900 placeholder-slate-400 border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/10")
            }`}
          />
          <Search className={`absolute top-4 left-4 ${isDark ? "text-white/50 group-focus-within:text-white" : (isPurple ? "text-black/50 group-focus-within:text-black" : "text-slate-400 group-focus-within:text-primary")} transition-colors`} size={18} />
          <div className={`absolute top-3.5 right-3.5 px-1.5 py-0.5 rounded border ${isDark ? "border-white/30 text-white/50" : (isPurple ? "border-black/30 text-black/50" : "border-slate-200 text-slate-400")} text-[10px] font-mono hidden sm:flex items-center gap-1`}>
             <Command size={10} /> K
          </div>
        </div>
      </div>

      <div className="space-y-3 mt-2">
        {filteredData.map(({ technology, topics = [] }) => {
          let globalFileCount = 0;
          return (
            <div key={technology} className="mb-2">
              {!hideTechHeader && (
                <div
                  className={`flex justify-between items-center cursor-pointer px-5 py-4 rounded-[20px] transition-all duration-300 group ${
                    activeTech === technology
                      ? (isDark || isPurple ? "bg-white/20 text-white shadow-xl border border-white/30" : "bg-primary text-white shadow-xl")
                      : (isDark || isPurple ? "text-white/60 hover:text-white hover:bg-white/10" : "text-slate-600 hover:bg-slate-100")
                  }`}
                  onClick={() => toggleTech(technology)}
                >
                  <div className="flex items-center gap-4">
                     <div className={`w-2 h-2 rounded-full ${activeTech === technology ? (isDark || isPurple ? "bg-white animate-pulse" : "bg-white animate-pulse") : (isDark || isPurple ? "bg-white/40 group-hover:bg-white" : "bg-slate-300 group-hover:bg-primary")}`}></div>
                     <span className="font-black text-[11px] uppercase tracking-[0.2em]">{technology}</span>
                  </div>
                  {openTech[technology] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                </div>
              )}

              {openTech[technology] && (
                <div className={`${hideTechHeader ? "" : "ml-4 mt-3 border-l-2 border-white/20 pl-4"} space-y-3 animate-fadeIn`}>
                  {topics.map(({ topic, subtopics = [] }) => (
                    <div key={topic} className="py-1">
                      <div
                        className={`flex items-center justify-between cursor-pointer transition-all duration-200 py-2.5 px-3 rounded-xl ${
                          isDark || isPurple ? "text-white/70 hover:text-white hover:bg-white/10" : "text-slate-500 hover:text-primary hover:bg-primary/5"
                        }`}
                        onClick={() => toggleTopic(topic)}
                      >
                        <span className="flex items-center text-[10px] font-black uppercase tracking-[0.2em] leading-relaxed">
                          {topic}
                        </span>
                        {openTopic[topic] ? <ChevronDown size={14} className="opacity-50" /> : <ChevronRight size={14} className="opacity-50" />}
                      </div>

                      {openTopic[topic] && (
                        <ul className="ml-2 mt-2 space-y-1.5">
                          {subtopics.map((sub, idx) => {
                            const currentFileIndex = globalFileCount++;

                            // TSG Nexus: the server owns the lock state. Legacy: the
                            // shared rule ("Level 1-5" free, else the first two documents)
                            // — the same function the topic navigation uses, so a topic
                            // is never locked here and open in Previous/Next or vice versa.
                            const nexusState = nexusMode && accessStates ? accessStates[sub.file] : null;
                            const isLocked = nexusMode
                              ? nexusState === 'LOCKED'
                              : legacyIsLocked(sub.file, currentFileIndex, hasFullAccess);
                            const usesPaidUnit = nexusMode && nexusState === 'AVAILABLE';
                            const isSelected = selectedFile === sub.file || (sub.file.startsWith('/') && selectedFile === sub.file.substring(1));
                            const isCompleted = (completionData[technology] || []).includes(sub.file);
                            
                            const statusColor = isLocked
                              ? "bg-amber-400"
                              : isCompleted 
                                ? "bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.5)]" 
                                : isSelected 
                                  ? (isDark || isPurple ? "bg-white shadow-[0_0_8px_rgba(255,255,255,0.5)] animate-pulse" : "bg-primary animate-pulse shadow-[0_0_8px_rgba(91,33,182,0.5)]")
                                  : (isDark || isPurple ? "bg-white/20 group-hover:bg-white/40" : "bg-slate-300 group-hover:bg-primary/40");

                            return (
                              <li
                                key={idx}
                                onClick={() => onSelectSubtopic(sub.file, isLocked, technology, topic, sub.title)}
                                className={`group cursor-pointer transition-all duration-300 flex items-center justify-between p-3 rounded-2xl ${
                                  isSelected
                                    ? (isDark || isPurple ? "bg-white/20 text-white border border-white/30 shadow-lg" : "bg-primary/10 text-primary border border-primary/20 shadow-md")
                                    : isLocked
                                      ? (isDark || isPurple ? "bg-black/20 text-white/30 border border-white/5 cursor-not-allowed" : "bg-slate-100 text-slate-300 border border-slate-100 cursor-not-allowed")
                                      : (isDark || isPurple ? "hover:bg-white/5 text-white/60 hover:text-white border border-transparent" : "hover:bg-white text-slate-600 border border-transparent hover:shadow-sm")
                                }`}
                              >
                                <div className="flex items-center gap-3 overflow-hidden">
                                  <div className={`w-2 h-2 rounded-full flex-shrink-0 transition-all duration-300 ${statusColor}`}></div>
                                  <div className="min-w-0">
                                    <span className={`block text-[13px] line-clamp-1 ${isSelected ? "font-black" : "font-medium"}`}>
                                      {sub.title}
                                    </span>
                                    {usesPaidUnit && (
                                      <span className={`block text-[10px] font-bold tracking-wide ${isDark || isPurple ? "text-white/40" : "text-slate-400"}`}>
                                        uses 1 paid page
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {isLocked && <Lock size={12} className="text-amber-400 opacity-50" />}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Branding Footer */}
      <div className={`mt-auto pt-10 pb-4 border-t ${isDark ? "border-white/10" : "border-slate-200"}`}>
        <div 
          className="flex flex-col items-center justify-center cursor-pointer group"
          onClick={() => window.open("https://www.theskillgenie.com", "_blank")}
        >
          <span className={`text-[10px] font-bold uppercase tracking-[0.2em] mb-1 ${isDark ? "text-white/40" : "text-slate-400"}`}>Powered by</span>
          <div className="flex items-center gap-1.5">
            <span className={`text-xs font-black uppercase tracking-tight ${isDark ? "text-white" : "bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"}`}>
              theskillgenie
            </span>
          </div>
        </div>
      </div>
    </div>
   );
  };

export default SidebarTreeView;




